import { useEffect, useRef, useState } from 'react';
import { useStore } from '../context/StoreContext';
import { useTheme } from '../shared/hooks/useTheme';
import { Icon } from '../shared/ui/Icon';
import { categoryName } from '../entities/product/helpers';

export function Header() {
  const {
    brand, page, category, categories, cartCount, search, searchOpen, mobileMenuOpen, user, settings,
    resourceStatus, resourceErrors, fetchCategories,
    go, browseCategory, browseDeals, browseFavorites, showAccount,
    setSearch, setSearchOpen, setPage, setMobileMenuOpen, openAuth,
  } = useStore();
  const { theme, toggleTheme } = useTheme();
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const categoryMenuRef = useRef(null);
  const categoryTriggerRef = useRef(null);
  const hoverTimer = useRef(null);
  const categoryDataLoading = resourceStatus.categories === 'loading';
  const categoryDataFailed = resourceStatus.categories === 'error';
  const hasCategoryPanel = categoryDataLoading || categoryDataFailed || categories.length > 0;
  const announcement = settings?.announcementBarText?.en
    || settings?.announcementBarText
    || settings?.announcementText?.en
    || settings?.announcementText
    || '';
  const contactPhone = settings?.contact?.phone || settings?.contactPhone || '';
  const tagline = settings?.tagline?.en || settings?.tagline || '';

  useEffect(() => {
    setCategoryMenuOpen(false);
    setMobileCategoriesOpen(false);
  }, [page, category]);

  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  useEffect(() => {
    const closeOutside = (event) => {
      if (!categoryMenuRef.current?.contains(event.target)) setCategoryMenuOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, []);

  function queueMenuState(open, delay) {
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setCategoryMenuOpen(open), delay);
  }

  function openCategory(slug = '') {
    setCategoryMenuOpen(false);
    setMobileCategoriesOpen(false);
    browseCategory(slug);
  }

  function handleCategoryKeyDown(event) {
    if (event.key === 'Escape') {
      setCategoryMenuOpen(false);
      categoryTriggerRef.current?.focus();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setCategoryMenuOpen(true);
      window.setTimeout(() => {
        const items = categoryMenuRef.current?.querySelectorAll('[role="menuitem"]');
        if (!items?.length) return;
        const targetIndex = event.key === 'ArrowDown' ? 0 : items.length - 1;
        items[targetIndex].focus();
      }, 0);
    }
  }

  function handleMenuItemKeyDown(event) {
    const items = [...(categoryMenuRef.current?.querySelectorAll('[role="menuitem"]') || [])];
    const index = items.indexOf(event.currentTarget);
    if (event.key === 'Escape') {
      event.preventDefault();
      setCategoryMenuOpen(false);
      categoryTriggerRef.current?.focus();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const offset = event.key === 'ArrowDown' ? 1 : -1;
      items[(index + offset + items.length) % items.length]?.focus();
    }
  }

  return (
    <>
      <div className="utility-bar">
        {announcement && <span>{announcement}</span>}
        {contactPhone && <span><a href={`tel:${contactPhone}`}>{contactPhone}</a></span>}
      </div>
      <header className="site-header">
        <button className="brand-mark" onClick={() => go('home')} aria-label={`${brand} home`}>
          <img className="brand-logo" src={`${process.env.PUBLIC_URL}/technomax-logo.png`} alt="" />
          <span>{brand.replace(/max/i, '')}</span>
          <b>{brand.match(/max/i)?.[0] || ''}</b>
        </button>
        {tagline && <p className="brand-promise">{tagline}</p>}
        <nav className={`primary-nav ${mobileMenuOpen ? 'is-open' : ''}`} aria-label="Main navigation">
          <button className="nav-close" onClick={() => setMobileMenuOpen(false)}>
            <span>Close menu</span><Icon>×</Icon>
          </button>
          <button className={page === 'home' ? 'active' : ''} aria-current={page === 'home' ? 'page' : undefined} onClick={() => go('home')}>Home</button>
          <div
            className={`category-menu-shell ${categoryMenuOpen ? 'is-open' : ''}`}
            ref={categoryMenuRef}
            onMouseEnter={() => queueMenuState(true, 150)}
            onMouseLeave={() => queueMenuState(false, 200)}
          >
            <button
              ref={categoryTriggerRef}
              type="button"
              className={`category-menu-trigger ${page === 'catalog' && category ? 'active' : ''}`}
              aria-haspopup={hasCategoryPanel ? 'menu' : undefined}
              aria-expanded={hasCategoryPanel ? categoryMenuOpen || mobileCategoriesOpen : undefined}
              onClick={() => {
                if (!hasCategoryPanel) return openCategory();
                if (window.matchMedia?.('(max-width: 1150px)').matches) {
                  setMobileCategoriesOpen((open) => !open);
                } else {
                  setCategoryMenuOpen((open) => !open);
                }
              }}
              onKeyDown={handleCategoryKeyDown}
            >
              Categories {hasCategoryPanel && <Icon>⌄</Icon>}
            </button>
            {hasCategoryPanel && !mobileCategoriesOpen && (
              <div className={`category-menu-panel ${categoryMenuOpen ? 'is-visible' : ''} ${mobileCategoriesOpen ? 'mobile-is-visible' : ''}`}>
                <div className={`category-menu-items ${categories.length > 8 ? 'mega-menu' : ''}`} role="menu" aria-label="Product categories">
                  {categoryDataLoading && Array.from({ length: 4 }, (_, index) => (
                    <span className="category-menu-skeleton" key={index} aria-hidden="true" />
                  ))}
                  {categoryDataFailed && (
                    <div className="category-menu-error">
                      <span>{resourceErrors.categories || 'Categories could not be loaded.'}</span>
                      <button type="button" role="menuitem" onKeyDown={handleMenuItemKeyDown} onClick={() => fetchCategories({ forceRefresh: true }).catch(() => {})}>Retry</button>
                    </div>
                  )}
                  {!categoryDataLoading && !categoryDataFailed && (
                    <>
                      <button type="button" role="menuitem" className={!category ? 'current' : ''} onKeyDown={handleMenuItemKeyDown} onClick={() => openCategory()}>
                        <span>All products</span>
                      </button>
                      {categories.map((item) => (
                        <button
                          type="button"
                          role="menuitem"
                          key={item.slug}
                          className={category === item.slug ? 'current' : ''}
                          aria-current={page === 'catalog' && category === item.slug ? 'page' : undefined}
                          onKeyDown={handleMenuItemKeyDown}
                          onClick={() => openCategory(item.slug)}
                        >
                          <span className="category-menu-name">
                            {item.imageUrl ? <img src={item.imageUrl} alt="" /> : item.icon ? <Icon>{item.icon}</Icon> : null}
                            {categoryName(item)}
                          </span>
                          <small>{item.count ?? 0}</small>
                        </button>
                      ))}
                      <button type="button" role="menuitem" className="category-menu-all" onKeyDown={handleMenuItemKeyDown} onClick={() => openCategory()}>
                        View all categories <Icon>→</Icon>
                      </button>
                    </>
                  )}
                  {!categoryDataLoading && !categoryDataFailed && categories.length === 0 && (
                    <button type="button" role="menuitem" onKeyDown={handleMenuItemKeyDown} onClick={() => openCategory()}>
                      No categories available
                    </button>
                  )}
                </div>
              </div>
            )}
            {mobileCategoriesOpen && (
              <div className="mobile-category-accordion">
                {categoryDataLoading && Array.from({ length: 4 }, (_, index) => <span className="category-menu-skeleton" key={index} aria-hidden="true" />)}
                {categoryDataFailed && (
                  <div className="category-menu-error">
                    <span>{resourceErrors.categories || 'Categories could not be loaded.'}</span>
                    <button type="button" onClick={() => fetchCategories({ forceRefresh: true }).catch(() => {})}>Retry</button>
                  </div>
                )}
                {!categoryDataLoading && !categoryDataFailed && categories.length > 0 && (
                  <>
                    <button type="button" onClick={() => openCategory()}>All products</button>
                    {categories.map((item) => (
                      <button type="button" key={item.slug} onClick={() => openCategory(item.slug)}>
                        {categoryName(item)}<small>{item.count ?? 0}</small>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
          <button onClick={browseDeals}>Deals</button>
          <button onClick={() => browseCategory()}>Brands</button>
          <button onClick={() => browseCategory()}>Blog</button>
          {user?.role === 'admin' && (
            <button className="admin-nav-link" onClick={() => go('admin')}>
              👑 Admin Panel
            </button>
          )}
        </nav>
        <form
          className={`header-search ${searchOpen ? 'is-open' : ''}`}
          onSubmit={(e) => { e.preventDefault(); browseCategory(); }}
        >
          <input
            aria-label="Search products"
            placeholder="Search products, brands..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); if (e.target.value) setPage('catalog'); }}
            onFocus={() => setSearchOpen(true)}
          />
          <button
            type="button"
            aria-label="Search"
            onClick={() => {
              if (window.matchMedia('(max-width: 1150px)').matches) {
                setSearchOpen(!searchOpen);
              } else {
                browseCategory();
              }
            }}
          >
            <Icon>⌕</Icon>
          </button>
        </form>
        <div className="header-actions">
          {user?.role === 'admin' && (
            <button
              className="admin-header-btn"
              onClick={() => go('admin')}
              title="Open Admin Panel"
            >
              👑 Admin
            </button>
          )}
          <button
            className="icon-button theme-toggle"
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
            onClick={toggleTheme}
          >
            <Icon>{theme === 'dark' ? '☼' : '◐'}</Icon>
            <span className="theme-label">{theme === 'dark' ? 'DARK MODE' : 'LIGHT MODE'}</span>
          </button>
          <button className="icon-button user-action" aria-label="Account" title="Account" onClick={showAccount}>
            <Icon>♙</Icon>
          </button>
          <button
            className="icon-button favorite-action"
            aria-label="Favorites"
            title="Favorites"
            onClick={() => user ? browseFavorites() : openAuth('login')}
          >
            <Icon>♡</Icon>
          </button>
          <button
            className="icon-button cart-action"
            aria-label={`Shopping cart, ${cartCount} items`}
            title="Shopping cart"
            onClick={() => go('cart')}
          >
            <Icon>▱</Icon>
            {cartCount > 0 && <span key={cartCount} className="cart-count">{cartCount}</span>}
          </button>
          <button
            className="icon-button menu-toggle"
            aria-label="Toggle navigation"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Icon>{mobileMenuOpen ? '×' : '☰'}</Icon>
          </button>
        </div>
      </header>
    </>
  );
}
